import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { LanguageFlag } from "./LanguageFlag";
import {
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  normalizeLanguage,
} from "../i18n/languages";

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const buttonRef = useRef(null);
  const current = normalizeLanguage(i18n.language);

  const updateMenuPosition = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    setMenuStyle({
      position: "fixed",
      top: rect.bottom + 8,
      left: Math.max(12, rect.right - 200),
      minWidth: 200,
      zIndex: 10050,
    });
  };

  useEffect(() => {
    if (!open) return;

    updateMenuPosition();

    const handlePointerDown = (event) => {
      const menu = document.getElementById("nav-lang-menu-portal");
      if (
        buttonRef.current?.contains(event.target) ||
        menu?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    const handleLayoutChange = () => updateMenuPosition();

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    window.addEventListener("resize", handleLayoutChange);
    window.addEventListener("scroll", handleLayoutChange, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
      window.removeEventListener("resize", handleLayoutChange);
      window.removeEventListener("scroll", handleLayoutChange, true);
    };
  }, [open]);

  const selectLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setOpen(false);
  };

  const getLanguageLabel = (lang) =>
    t(`languageSwitcher.options.${lang}`, {
      defaultValue: LANGUAGE_NAMES[lang],
    });

  const menu =
    open && menuStyle
      ? ReactDOM.createPortal(
          <ul
            id="nav-lang-menu-portal"
            className="nav-lang-menu nav-lang-menu--portal"
            style={menuStyle}
            role="listbox"
            aria-label={t("languageSwitcher.menuLabel")}
          >
            {SUPPORTED_LANGUAGES.map((lang) => {
              const selected = current === lang;
              return (
                <li key={lang} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={selected ? "is-active" : undefined}
                    onClick={() => selectLanguage(lang)}
                  >
                    <LanguageFlag lang={lang} className="nav-lang-flag-svg" />
                    <span>{getLanguageLabel(lang)}</span>
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )
      : null;

  return (
    <li className={`nav-lang-switcher-wrap${open ? " is-open" : ""}`}>
      <button
        ref={buttonRef}
        type="button"
        className="nav-lang-switcher"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t("languageSwitcher.ariaLabel", {
          language: getLanguageLabel(current),
        })}
      >
        <LanguageFlag lang={current} className="nav-lang-flag-svg nav-lang-flag-svg--current" />
      </button>
      {menu}
    </li>
  );
}
